import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useContextMenu } from "../../../../contexts/RightClickMenuContext";
import { useTeamPresence } from "../../../../contexts/TeamPresenceContext";
import { useLayout } from "../../../../contexts/LayoutContext";
import {
  getThreads,
  renameThread,
  deleteThread,
} from "../../../../../api/chat";
import {
  getConversations,
  archiveConversation,
  getArchivedConversations,
  muteConversation,
  leaveGroup,
} from "../../../../../api/teamChat";
import { Archive, BellOff, Bell, LogOut, Pencil, Trash2 } from "lucide-react";

const useChatSidebarData = () => {
  const { currentAppPage } = useLayout();
  const navigate = useNavigate();
  const {
    onlineUsers,
    unreadCounts,
    mutedConversations,
    updateMutedConversation,
  } = useTeamPresence();

  const [threadsByType, setThreadsByType] = useState({
    private: [],
    public: [],
  });
  const [teamConversations, setTeamConversations] = useState({
    channels: [],
    groups: [],
    dms: [],
  });
  const [archivedIds, setArchivedIds] = useState([]);
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [currentTeamId, setCurrentTeamId] = useState(null);
  const [expandedDirs, setExpandedDirs] = useState({
    private: true,
    public: false,
    team: false,
  });
  const [expandedTeamSubs, setExpandedTeamSubs] = useState({
    groups: true,
    dms: true,
    archived: false,
  });
  const [threadRefreshKey, setThreadRefreshKey] = useState(0);

  /* ── Data loading ── */
  const loadThreads = useCallback(async () => {
    try {
      const [privRes, pubRes] = await Promise.all([
        getThreads(0, "private"),
        getThreads(0, "public"),
      ]);
      setThreadsByType({
        private: privRes?.data?.threads || [],
        public: pubRes?.data?.threads || [],
      });
    } catch (e) {
      console.error("Error loading chat threads:", e);
      setThreadsByType({ private: [], public: [] });
    }
    setThreadRefreshKey((prev) => prev + 1);
  }, []);

  const loadTeamConversations = useCallback(async () => {
    try {
      const res = await getConversations();
      const data = res?.data || {};
      setTeamConversations({
        channels: data.channels || [],
        groups: data.groups || [],
        dms: data.dms || [],
      });
    } catch (e) {
      console.error("Error loading team conversations:", e);
      setTeamConversations({ channels: [], groups: [], dms: [] });
    }
  }, []);

  useEffect(() => {
    loadThreads();
    loadTeamConversations();
  }, [loadThreads, loadTeamConversations]);

  useEffect(() => {
    getArchivedConversations()
      .then((res) => setArchivedIds(res?.data?.archived_conversations || []))
      .catch((e) => console.error("Failed to load archived:", e));
  }, []);

  /* ── Reset on page change ── */
  useEffect(() => {
    if (currentAppPage !== "/chat") {
      setCurrentThreadId(null);
      setCurrentTeamId(null);
    }
  }, [currentAppPage]);

  /* ── Clear active state when another section becomes active ── */
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section && e.detail.section !== "chat") {
        setCurrentThreadId(null);
        setCurrentTeamId(null);
      }
    };
    window.addEventListener("sidebar:active-section-changed", handler);
    return () =>
      window.removeEventListener("sidebar:active-section-changed", handler);
  }, []);

  /* ── Refresh listeners ── */
  useEffect(() => {
    const handler = () => {
      loadThreads();
      loadTeamConversations();
    };
    window.addEventListener("sidebar:refresh-threads", handler);
    return () => window.removeEventListener("sidebar:refresh-threads", handler);
  }, [loadThreads, loadTeamConversations]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.threadId !== undefined)
        setCurrentThreadId(e.detail.threadId);
      if (e.detail?.teamId !== undefined) setCurrentTeamId(e.detail.teamId);
    };
    window.addEventListener("sidebar:select-thread", handler);
    return () => window.removeEventListener("sidebar:select-thread", handler);
  }, []);

  useEffect(() => {
    const handler = () => loadTeamConversations();
    window.addEventListener("sidebar:refresh-team-conversations", handler);
    return () =>
      window.removeEventListener("sidebar:refresh-team-conversations", handler);
  }, [loadTeamConversations]);

  /* ── Right-click menu for team conversations ── */
  useContextMenu("team-conversation", [
    {
      label: "Mute",
      icon: BellOff,
      action: async (payload) => {
        if (!payload?.team_id) return;
        const isMuted = mutedConversations.includes(payload.team_id);
        await muteConversation(payload.team_id, !isMuted);
        updateMutedConversation(payload.team_id, !isMuted);
      },
      getLabel: (payload) =>
        mutedConversations.includes(payload?.team_id) ? "Unmute" : "Mute",
      getIcon: (payload) =>
        mutedConversations.includes(payload?.team_id) ? Bell : BellOff,
    },
    {
      label: "Archive",
      icon: Archive,
      action: async (payload) => {
        if (!payload?.team_id) return;
        const isArchived = archivedIds.includes(payload.team_id);
        await archiveConversation(payload.team_id, !isArchived);
        setArchivedIds((prev) =>
          isArchived
            ? prev.filter((id) => id !== payload.team_id)
            : [...prev, payload.team_id],
        );
      },
      getLabel: (payload) =>
        archivedIds.includes(payload?.team_id) ? "Unarchive" : "Archive",
    },
    {
      label: "Leave Group",
      icon: LogOut,
      variant: "danger",
      action: async (payload) => {
        if (!payload?.team_id) return;
        try {
          await leaveGroup(payload.team_id);
          loadTeamConversations();
          if (currentTeamId === payload.team_id) {
            setCurrentTeamId(null);
          }
        } catch (e) {
          console.error("Failed to leave group:", e);
        }
      },
      hidden: (payload) =>
        payload?.type === "channel" || payload?.type === "dm",
    },
  ]);
  /* ── Right-click menu for private/public threads ── */
  useContextMenu("chat-thread", [
    {
      label: "Rename",
      icon: Pencil,
      action: (payload) => {
        if (!payload?.thread_id) return;
        window.dispatchEvent(
          new CustomEvent("chat-thread:start-rename", {
            detail: { threadId: payload.thread_id },
          }),
        );
      },
    },
    {
      label: "Delete",
      icon: Trash2,
      variant: "danger",
      action: (payload) => {
        if (!payload?.thread_id) return;
        window.dispatchEvent(
          new CustomEvent("chat-thread:start-delete", {
            detail: { threadId: payload.thread_id },
          }),
        );
      },
    },
  ]);
  /* ── Mapped data ── */
  const mappedByType = useMemo(
    () => ({
      private: threadsByType.private.map((t) => ({
        id: t.thread_id,
        label: t.title || "Thread",
        thread_type: t.thread_type || "private",
      })),
      public: threadsByType.public.map((t) => ({
        id: t.thread_id,
        label: t.title || "Thread",
        thread_type: "public",
      })),
    }),
    [threadsByType],
  );

  const allTeamMapped = useMemo(() => {
    const items = [];
    for (const ch of teamConversations.channels) {
      items.push({
        id: ch.team_id,
        label: ch.name || "Channel",
        thread_type: "team",
        isTeam: true,
      });
    }
    for (const g of teamConversations.groups) {
      items.push({
        id: g.team_id,
        label: g.name || "Group",
        thread_type: "team",
        isTeam: true,
      });
    }
    for (const dm of teamConversations.dms) {
      items.push({
        id: dm.team_id,
        label: dm.dm_display_name || "DM",
        thread_type: "team",
        isTeam: true,
      });
    }
    return items;
  }, [teamConversations]);

  const allMapped = useMemo(
    () => [...mappedByType.private, ...mappedByType.public, ...allTeamMapped],
    [mappedByType, allTeamMapped],
  );

  /* ── Breadcrumb ── */
  const emitChatBreadcrumb = useCallback(() => {
    if (currentAppPage !== "/chat") return;
    const activeId = currentTeamId || currentThreadId;
    const activeThread = allMapped.find((t) => t.id === activeId);
    window.dispatchEvent(
      new CustomEvent("breadcrumb:update", {
        detail: {
          section: "/chat",
          subLabel: activeThread?.label || null,
          subIcon: null,
          hasAdd: true,
          addKind: "chat",
          addLabel: null,
        },
      }),
    );
  }, [currentThreadId, currentTeamId, allMapped, currentAppPage]);

  useEffect(() => {
    emitChatBreadcrumb();
  }, [emitChatBreadcrumb]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section === "/chat") emitChatBreadcrumb();
    };
    window.addEventListener("breadcrumb:request", handler);
    return () => window.removeEventListener("breadcrumb:request", handler);
  }, [emitChatBreadcrumb]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section === "/chat") {
        setCurrentThreadId(null);
        setCurrentTeamId(null);
        window.dispatchEvent(
          new CustomEvent("sidebar:chat-thread-selected", {
            detail: { threadId: null, thread_type: "private" },
          }),
        );
        window.dispatchEvent(
          new CustomEvent("sidebar:active-section-changed", {
            detail: { section: "chat" },
          }),
        );
        if (currentAppPage !== "/chat") navigate("/chat");
      }
    };
    window.addEventListener("breadcrumb:add", handler);
    return () => window.removeEventListener("breadcrumb:add", handler);
  }, [currentAppPage, navigate]);

  /* ── Selection handlers ── */
  const handleSelect = useCallback(
    (threadId, thread_type = "private") => {
      setCurrentThreadId(threadId);
      setCurrentTeamId(null);
      window.dispatchEvent(
        new CustomEvent("sidebar:active-section-changed", {
          detail: { section: "chat" },
        }),
      );
      if (currentAppPage !== "/chat") {
        navigate("/chat");
        requestAnimationFrame(() => {
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent("sidebar:chat-thread-selected", {
                detail: { threadId, thread_type },
              }),
            );
          }, 50);
        });
      } else {
        window.dispatchEvent(
          new CustomEvent("sidebar:chat-thread-selected", {
            detail: { threadId, thread_type },
          }),
        );
      }
    },
    [currentAppPage, navigate],
  );

  const handleSelectTeam = useCallback(
    (teamId) => {
      setCurrentTeamId(teamId);
      setCurrentThreadId(null);
      window.dispatchEvent(
        new CustomEvent("sidebar:active-section-changed", {
          detail: { section: "chat" },
        }),
      );
      if (currentAppPage !== "/chat") {
        navigate("/chat");
        requestAnimationFrame(() => {
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent("sidebar:chat-thread-selected", {
                detail: {
                  threadId: null,
                  thread_type: "team",
                  team_id: teamId,
                },
              }),
            );
          }, 50);
        });
      } else {
        window.dispatchEvent(
          new CustomEvent("sidebar:chat-thread-selected", {
            detail: { threadId: null, thread_type: "team", team_id: teamId },
          }),
        );
      }
    },
    [currentAppPage, navigate],
  );

  const handleNew = useCallback(
    (thread_type = "private") => {
      setCurrentThreadId(null);
      setCurrentTeamId(null);
      window.dispatchEvent(
        new CustomEvent("sidebar:chat-thread-selected", {
          detail: { threadId: null, thread_type },
        }),
      );
      window.dispatchEvent(
        new CustomEvent("sidebar:active-section-changed", {
          detail: { section: "chat" },
        }),
      );
      if (currentAppPage !== "/chat") navigate("/chat");
    },
    [currentAppPage, navigate],
  );

  const toggleDir = useCallback((key) => {
    setExpandedDirs((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const toggleTeamSub = useCallback((key) => {
    setExpandedTeamSubs((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  return {
    // presence
    onlineUsers,
    unreadCounts,
    mutedConversations,
    // state
    mappedByType,
    teamConversations,
    archivedIds,
    currentThreadId,
    currentTeamId,
    expandedDirs,
    expandedTeamSubs,
    threadRefreshKey,
    // actions
    handleSelect,
    handleSelectTeam,
    handleNew,
    toggleDir,
    toggleTeamSub,
    loadTeamConversations,
    loadThreads,
  };
};

export default useChatSidebarData;
